import type React from 'react';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage as ReactIntlFormattedMessage } from 'react-intl';

import type en from '../locales/en.json';

export type SgDesignSystemMessageId = keyof typeof en;

type SgFormattedMessageProps = Omit<MessageDescriptor, 'id'> & {
  id: SgDesignSystemMessageId;
  values?: Record<string, string | number | boolean | Date | null | undefined>;
  tagName?: React.ElementType;
  children?: (nodes: React.ReactNode[]) => React.ReactNode;
};

/**
 * FormattedMessage component for the SG Design System with strict type checking.
 *
 * This component ensures that only valid message IDs from the design system locales
 * can be used, providing compile-time safety and autocomplete support.
 *
 * @example
 * <SgFormattedMessage id="picker.limit" />
 */
export function SgFormattedMessage({ id, ...rest }: SgFormattedMessageProps): React.ReactElement {
  /**
   * Using 'as any' here is acceptable for the following reasons:
   *
   * 1. The global FormatjsIntl.Message override in src/types/intl.d.ts restricts
   *    the 'id' prop to only accept keys from the main application's locale files.
   *
   * 2. The design system has its own separate locale files and needs to use
   *    different message IDs that are not present in the main application.
   *
   * 3. Type safety is fully enforced at this component's public API level:
   *    - The SgFormattedMessageProps type restricts 'id' to SgDesignSystemMessageId
   *    - TypeScript will show an error if an invalid key is used
   *    - IDE autocomplete works correctly for design system message keys
   *
   * 4. This approach keeps the design system autonomous and portable:
   *    - No need to modify hundreds of files in the main application
   *    - The design system can be easily extracted to another project
   *    - The 'as any' cast is localized to this single wrapper component
   *
   * Alternative solutions considered:
   * - Creating an AppFormattedMessage wrapper: Would require changing all FormattedMessage
   *   usages in the main app (hundreds of files)
   * - Removing global type override: Would lose type safety in the main application
   */
  // oxlint-disable-next-line no-explicit-any -- See detailed explanation above
  return <ReactIntlFormattedMessage id={id as any} {...rest} />;
}
